// src/components/admin/batches/BatchInspectorDrawer.tsx
import React, { useEffect, useState } from "react";
import { Batch } from "src/store/adminBatchStore";
import { Button } from "src/components/ui/Button";
import { Badge } from "src/components/ui/Badge";
import { format } from "date-fns";
import { supabase } from "src/lib/supabaseClient";
import {
  User,
  Calendar,
  BookOpen,
  PlusCircle,
  Edit,
  AlertTriangle,
  CheckCircle,
  Ban,
  Loader2,
  Clock,
} from "lucide-react";

interface BatchObjective {
  id: string;
  title: string;
  learning_objective?: string;
  status: string;
}

interface BatchInspectorDrawerProps {
  batch: Batch | null;
  onEdit: (batchId: string) => void;
  onCancel: (batchId: string) => void;
  onComplete: (batchId: string) => void;
  onExtend: (batchId: string) => void;
  onAssignTutor: (batchId: string) => void;
}

export const BatchInspectorDrawer: React.FC<BatchInspectorDrawerProps> = ({
  batch,
  onEdit,
  onCancel,
  onComplete,
  onExtend,
  onAssignTutor,
}) => {
  const [objectives, setObjectives] = useState<BatchObjective[]>([]);
  const [isLoadingObjectives, setIsLoadingObjectives] = useState(false);

  useEffect(() => {
    if (!batch?.id) {
      setObjectives([]);
      return;
    }

    let isMounted = true;
    const fetchBatchObjectives = async () => {
      setIsLoadingObjectives(true);
      try {
        // Query batch curriculum units and their objectives
        const { data: units, error: unitError } = await supabase
          .from("batch_curriculum_units")
          .select("id")
          .eq("batch_id", batch.id)
          .eq("status", "active");

        if (unitError || !units || units.length === 0) {
          if (isMounted) setObjectives([]);
          return;
        }

        const unitIds = units.map((u) => u.id);
        const { data: objs, error: objError } = await supabase
          .from("batch_curriculum_objectives")
          .select(`
            id, status, display_order,
            objective:curriculum_objectives(title, learning_objective)
          `)
          .in("batch_curriculum_unit_id", unitIds)
          .order("display_order");

        if (objError || !objs) {
          if (isMounted) setObjectives([]);
          return;
        }

        if (isMounted) {
          interface ObjectiveJoin {
            title?: string;
            learning_objective?: string;
          }
          const formatted = objs.map((entry: { id: string; status: string; objective: unknown }) => {
            const objData = entry.objective as ObjectiveJoin | null;
            return {
              id: entry.id,
              title: objData?.title || "Curriculum Objective",
              learning_objective: objData?.learning_objective,
              status: entry.status || "active",
            };
          });
          setObjectives(formatted);
        }
      } catch (err) {
        console.warn("Could not fetch batch objectives:", err);
        if (isMounted) setObjectives([]);
      } finally {
        if (isMounted) setIsLoadingObjectives(false);
      }
    };

    void fetchBatchObjectives();

    return () => {
      isMounted = false;
    };
  }, [batch?.id]);

  if (!batch) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-500 shadow-sm flex flex-col items-center justify-center min-h-[400px]">
        <BookOpen className="w-10 h-10 text-slate-300 mb-3" />
        <h3 className="text-sm font-semibold text-slate-800">No Batch Selected</h3>
        <p className="text-xs text-slate-500 mt-1 max-w-xs">
          Select a batch from the queue on the left to inspect student details, curriculum progress, and session balances.
        </p>
      </div>
    );
  }

  const total = batch.total_sessions ?? 0;
  const remaining = batch.remaining_sessions ?? 0;
  const completed = Math.max(0, total - remaining);
  const consumptionPercent = total > 0 ? Math.round((completed / total) * 100) : 0;
  const isLowBalance = remaining <= 2 && remaining > 0 && batch.status === "active";
  const hasNoTutor = !batch.default_tutor_id || !batch.tutor_name;

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "—";
    try {
      return format(new Date(dateStr), "MMM d, yyyy");
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm flex flex-col overflow-hidden sticky top-6">
      {/* Header */}
      <div className="bg-[#faf9fe] p-5 border-b border-slate-200">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500 flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5 text-slate-400" />
                Created {formatDate(batch.created_at)}
              </span>
            </div>
            <h2 className="text-base font-bold text-slate-900 mt-1">
              {batch.name || batch.student_name || "Unnamed Batch"}
            </h2>
            <div className="text-xs text-slate-600 mt-0.5 flex flex-wrap items-center gap-1.5">
              {batch.student_name && (
                <>
                  <span className="font-semibold text-slate-800">
                    Student: {batch.student_name}
                  </span>
                  <span>&bull;</span>
                </>
              )}
              <span className="font-medium text-slate-700">{batch.subject_name || "Subject Not Set"}</span>
              <span>&bull;</span>
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-purple-50 text-purple-700 border border-purple-200">
                {batch.package_name || batch.package_type || "Tutoring Cohort"}
              </span>
            </div>
          </div>

          <div>
            {batch.status === "active" ? (
              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 flex items-center gap-1 text-xs py-0.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                Active
              </Badge>
            ) : batch.status === "completed" ? (
              <Badge variant="outline" className="bg-slate-100 text-slate-700 border-slate-200 text-xs py-0.5">
                Completed
              </Badge>
            ) : (
              <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200 text-xs py-0.5 capitalize">
                {batch.status.replace("_", " ")}
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* Scrollable Body */}
      <div className="p-5 space-y-5 text-xs text-slate-700 overflow-y-auto max-h-[540px]">
        {/* Block 1: Assigned Tutor Profile Card */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="font-bold text-slate-900 uppercase tracking-wider text-xs text-slate-500">
              Assigned Instructor
            </span>
            <Button
              variant="link"
              size="sm"
              onClick={() => onAssignTutor(batch.id)}
              className="p-0 h-auto text-xs text-purple-700 font-semibold hover:underline"
            >
              {hasNoTutor ? "Assign Tutor" : "Reassign Tutor"}
            </Button>
          </div>

          {hasNoTutor ? (
            <div className="p-3.5 rounded-lg border border-amber-300 bg-amber-50/70 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-amber-200 text-amber-900 font-bold flex items-center justify-center text-xs shrink-0">
                  ⚠️
                </div>
                <div>
                  <div className="font-bold text-slate-900 text-xs">No Instructor Assigned</div>
                  <div className="text-amber-800 text-xs mt-0.5">
                    Needs tutor dispatch to begin scheduling sessions.
                  </div>
                </div>
              </div>
              <Button
                size="sm"
                onClick={() => onAssignTutor(batch.id)}
                className="bg-purple-600 hover:bg-purple-700 text-white text-xs h-8 px-3 shrink-0"
              >
                Match Tutor
              </Button>
            </div>
          ) : (
            <div className="p-3.5 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-purple-600 text-white font-bold flex items-center justify-center text-xs shadow-2xs">
                  <User className="w-4 h-4" />
                </div>
                <div>
                  <div className="font-bold text-slate-900 text-xs">{batch.tutor_name}</div>
                  <div className="text-slate-500 text-xs mt-0.5 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3 text-emerald-600" />
                    Verified Instructor
                  </div>
                </div>
              </div>
              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">
                Assigned
              </Badge>
            </div>
          )}
        </div>

        {/* Block 2: Session Consumption Pacing */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="font-bold text-slate-900 uppercase tracking-wider text-xs text-slate-500">
              Session Consumption Pacing
            </span>
            <span
              className={`font-mono font-bold text-xs ${
                isLowBalance ? "text-rose-600" : "text-purple-700"
              }`}
            >
              {consumptionPercent}% Consumed
            </span>
          </div>

          <div className="p-3.5 rounded-lg border border-slate-200 bg-white space-y-3">
            <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
              <div
                className={`h-2 rounded-full transition-all duration-300 ${
                  isLowBalance
                    ? "bg-rose-500"
                    : consumptionPercent === 100
                    ? "bg-emerald-500"
                    : "bg-purple-600"
                }`}
                style={{ width: `${Math.min(100, consumptionPercent)}%` }}
              ></div>
            </div>

            <div className="grid grid-cols-3 gap-2 text-center pt-1 border-t border-slate-100">
              <div>
                <div className="text-slate-400 text-xs">Total Booked</div>
                <div className="font-bold font-mono text-slate-900 text-sm">{total}</div>
              </div>
              <div>
                <div className="text-slate-400 text-xs">Delivered</div>
                <div className="font-bold font-mono text-emerald-700 text-sm">{completed}</div>
              </div>
              <div>
                <div className="text-slate-400 text-xs">Remaining</div>
                <div
                  className={`font-bold font-mono text-sm ${
                    isLowBalance ? "text-rose-600" : "text-purple-700"
                  }`}
                >
                  {remaining}
                </div>
              </div>
            </div>

            {isLowBalance && (
              <div className="p-2 rounded bg-rose-50 border border-rose-200 text-rose-800 text-xs flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5 text-rose-600 shrink-0" />
                <span>
                  <strong>Low Balance:</strong> Only {remaining} session{remaining === 1 ? "" : "s"} left. Prompt renewal.
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Block 3: Curriculum Objectives Snapshot */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="font-bold text-slate-900 uppercase tracking-wider text-xs text-slate-500">
              Curriculum Objectives Snapshot
            </span>
            <span className="font-mono text-xs text-slate-500">
              {objectives.length} Goal{objectives.length === 1 ? "" : "s"}
            </span>
          </div>

          {isLoadingObjectives ? (
            <div className="py-4 text-center text-xs text-slate-500 flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin text-purple-600" />
              <span>Loading curriculum objectives...</span>
            </div>
          ) : objectives.length === 0 ? (
            <div className="p-3 rounded-lg border border-slate-200 bg-slate-50 text-slate-500 text-xs text-center">
              No individual objectives attached yet. Objectives are linked through active curriculum scopes.
            </div>
          ) : (
            <div className="space-y-2">
              {objectives.map((obj) => (
                <div
                  key={obj.id}
                  className="p-2.5 rounded-lg border border-slate-200 bg-slate-50/60 flex items-start justify-between gap-2"
                >
                  <div>
                    <div className="font-medium text-slate-900 text-xs">{obj.title}</div>
                    {obj.learning_objective && (
                      <div className="text-slate-500 text-xs mt-0.5">{obj.learning_objective}</div>
                    )}
                  </div>
                  <Badge
                    variant="outline"
                    className="capitalize text-xs font-mono bg-purple-50 text-purple-700 border-purple-200"
                  >
                    {obj.status}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Action Footer */}
      <div className="p-4 bg-slate-50 border-t border-slate-200 flex flex-wrap items-center justify-between gap-2">
        <div>
          {batch.status === "active" ? (
            <div className="flex items-center gap-1.5">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onCancel(batch.id)}
                className="text-xs text-rose-700 border-rose-200 hover:bg-rose-50"
              >
                <Ban className="w-3.5 h-3.5 mr-1" />
                Cancel
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onComplete(batch.id)}
                className="text-xs text-emerald-700 border-emerald-200 hover:bg-emerald-50"
              >
                <CheckCircle className="w-3.5 h-3.5 mr-1" />
                Complete
              </Button>
            </div>
          ) : (
            <span className="text-xs text-slate-500 flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" />
              Cohort Status: {batch.status}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onEdit(batch.id)}
            className="text-xs border-slate-300"
          >
            <Edit className="w-3.5 h-3.5 mr-1" />
            Edit
          </Button>

          {batch.status === "active" && (
            <Button
              size="sm"
              onClick={() => onExtend(batch.id)}
              className="bg-purple-600 hover:bg-purple-700 text-white text-xs shadow-2xs"
            >
              <PlusCircle className="w-3.5 h-3.5 mr-1" />
              Extend Sessions
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
